import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet-draw';
import type { RompArea } from '../../types';
import { simplifyPts } from '../../lib/rompimentos';

interface RompimentoMapEditorProps {
  initialAreas: RompArea[];
  onChange: (areas: RompArea[]) => void;
}

const AREA_STYLE: L.PathOptions = { color: '#dc2626', weight: 2, fillColor: '#dc2626', fillOpacity: 0.18 };
const DEFAULT_CENTER: [number, number] = [-23.5015, -47.4526];

function areaToLayer(a: RompArea): L.Layer {
  if (a.tipo === 'circulo') return L.circle([a.lat, a.lng], { radius: a.raio || 300, ...AREA_STYLE });
  const poly = L.polygon(
    (a.pontos || []).map((p) => [p.lat, p.lng] as [number, number]),
    AREA_STYLE
  );
  if (a.nome) (poly as any)._rompNome = a.nome;
  return poly;
}

export default function RompimentoMapEditor({ initialAreas, onChange }: RompimentoMapEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const drawnRef = useRef<L.FeatureGroup | null>(null);
  const searchMarkerRef = useRef<L.CircleMarker | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const readAreas = (): RompArea[] => {
    const drawn = drawnRef.current;
    const areas: RompArea[] = [];
    if (!drawn) return areas;
    drawn.eachLayer((layer: L.Layer) => {
      const anyLayer = layer as any;
      if (typeof anyLayer.getRadius === 'function' && typeof anyLayer.getLatLng === 'function') {
        const c = anyLayer.getLatLng();
        areas.push({ tipo: 'circulo', lat: c.lat, lng: c.lng, raio: Math.round(anyLayer.getRadius()) });
      } else if (layer instanceof L.Polygon) {
        const ring = (layer.getLatLngs()[0] as L.LatLng[]) || [];
        const pontos = simplifyPts(ring.map((p) => ({ lat: p.lat, lng: p.lng })));
        if (pontos.length >= 3) {
          const a: RompArea = { tipo: 'poligono', pontos };
          if (anyLayer._rompNome) a.nome = anyLayer._rompNome;
          areas.push(a);
        }
      }
    });
    return areas;
  };

  const emit = () => onChangeRef.current(readAreas());

  // Localização PT-BR do leaflet-draw
  const setupLocale = () => {
    const dl = (L as any).drawLocal;
    if (!dl) return;
    dl.draw.toolbar.actions.title = 'Cancelar desenho';
    dl.draw.toolbar.actions.text = 'Cancelar';
    dl.draw.toolbar.finish.title = 'Finalizar desenho';
    dl.draw.toolbar.finish.text = 'Finalizar';
    dl.draw.toolbar.undo.title = 'Apagar último ponto';
    dl.draw.toolbar.undo.text = 'Apagar último ponto';
    dl.draw.toolbar.buttons.circle = 'Desenhar círculo (afeta todos os lados)';
    dl.draw.toolbar.buttons.polygon = 'Desenhar polígono (área direcionada)';
    dl.draw.handlers.circle.tooltip.start = 'Clique e arraste para desenhar o círculo.';
    dl.draw.handlers.circle.radius = 'Raio';
    dl.draw.handlers.polygon.tooltip.start = 'Clique para começar a desenhar a área.';
    dl.draw.handlers.polygon.tooltip.cont = 'Clique para continuar desenhando.';
    dl.draw.handlers.polygon.tooltip.end = 'Clique no primeiro ponto para fechar a área.';
    dl.edit.toolbar.actions.save.title = 'Salvar alterações';
    dl.edit.toolbar.actions.save.text = 'Salvar';
    dl.edit.toolbar.actions.cancel.title = 'Cancelar edição';
    dl.edit.toolbar.actions.cancel.text = 'Cancelar';
    dl.edit.toolbar.buttons.edit = 'Editar áreas';
    dl.edit.toolbar.buttons.editDisabled = 'Nenhuma área para editar';
    dl.edit.toolbar.buttons.remove = 'Apagar área';
    dl.edit.toolbar.buttons.removeDisabled = 'Nenhuma área para apagar';
  };

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    setupLocale();

    const map = L.map(containerRef.current).setView(DEFAULT_CENTER, 12);
    mapRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);

    const drawn = new L.FeatureGroup();
    drawnRef.current = drawn;
    map.addLayer(drawn);

    const drawControl = new (L as any).Control.Draw({
      position: 'topleft',
      draw: {
        circle: { shapeOptions: AREA_STYLE, metric: true },
        polygon: { shapeOptions: AREA_STYLE, showArea: false, allowIntersection: false },
        rectangle: false,
        marker: false,
        polyline: false,
        circlemarker: false
      },
      edit: { featureGroup: drawn }
    });
    map.addControl(drawControl);

    (initialAreas || []).forEach((a) => drawn.addLayer(areaToLayer(a)));

    map.on((L as any).Draw.Event.CREATED, (e: any) => {
      drawn.addLayer(e.layer);
      emit();
    });
    map.on((L as any).Draw.Event.EDITED, emit);
    map.on((L as any).Draw.Event.DELETED, emit);

    setTimeout(() => {
      map.invalidateSize();
      if (initialAreas && initialAreas.length) {
        const b = drawn.getBounds();
        if (b.isValid()) map.fitBounds(b.pad(0.3));
      }
    }, 150);

    return () => {
      map.remove();
      mapRef.current = null;
      drawnRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = async (raw: string) => {
    const map = mapRef.current;
    if (!map || !raw.trim()) return;
    let lat: number | null = null;
    let lng: number | null = null;
    const m = raw.match(/(-?\d{1,3}\.\d+)\s*[,;\s]\s*(-?\d{1,3}\.\d+)/);
    if (m) {
      const a = parseFloat(m[1]);
      const b = parseFloat(m[2]);
      if (a >= -90 && a <= 90 && b >= -180 && b <= 180) {
        lat = a;
        lng = b;
      }
    }
    if (lat == null) {
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&accept-language=pt-BR&countrycodes=br&q=${encodeURIComponent(
          raw
        )}`;
        const res = await fetch(url);
        const arr = res.ok ? await res.json() : [];
        if (arr.length) {
          lat = parseFloat(arr[0].lat);
          lng = parseFloat(arr[0].lon);
        }
      } catch {
        /* ignore */
      }
    }
    if (lat == null || isNaN(lat) || lng == null || isNaN(lng)) {
      window.alert('Local não encontrado. Cole coordenadas no formato -23.288666, -47.646465.');
      return;
    }
    map.setView([lat, lng], 16);
    if (searchMarkerRef.current) map.removeLayer(searchMarkerRef.current);
    searchMarkerRef.current = L.circleMarker([lat, lng], {
      radius: 7,
      color: '#2563eb',
      weight: 3,
      fillColor: '#ffffff',
      fillOpacity: 1,
      interactive: false
    }).addTo(map);
    searchMarkerRef.current.bindTooltip('📍 Local buscado — desenhe a área aqui', { permanent: true, direction: 'top', offset: [0, -8] }).openTooltip();
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <input
          type="text"
          className="h-9 flex-1 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
          placeholder="Buscar endereço ou colar coordenadas e pressionar Enter..."
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              search((e.target as HTMLInputElement).value);
            }
          }}
        />
      </div>
      <div
        ref={containerRef}
        style={{ height: 340 }}
        className="w-full overflow-hidden rounded-xl border border-slate-200"
      />
    </div>
  );
}
